import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Switch,
    Tab,
    TextField,
    Typography,
} from "@mui/material";
import { Container } from "@mui/system";
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useMemo, useState } from "react";
import Specializations from "../components/Specializations";
import DoctorCategories from "../components/DoctorCategories";
import DoctorsList from "./DoctorsList";
import PageHeader from "../components/PageHeader";
import { DOCTORS_ROUTE } from "../utils/consts";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchDoctors, syncDoctorsFromMIS } from "../http/doctorAPI";
import { Context } from "..";
import useSnackbar from "../hooks/useSnackbar";
import MySnackbar from "../components/MySnackbar";

const Doctors = observer(() => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { clinic } = useContext(Context);
    const tabValues = useMemo(() => new Set(["1", "2", "3"]), []);
    const tabFromQuery = searchParams.get("tab");
    const initialValue = tabValues.has(tabFromQuery) ? tabFromQuery : "1";
    const [value, setValue] = useState(initialValue);
    const [doctors, setDoctors] = useState([]);
    const [showAll, setShowAll] = useState(
        clinic?.showAllDoctorsInBot || false,
    );
    const [twoColumns, setTwoColumns] = useState(
        clinic?.doctorButtonsInTwoColumns !== false,
    );
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [role, setRole] = useState("");
    const [syncing, setSyncing] = useState(false);
    const snackbar = useSnackbar(false, "", "success");

    useEffect(() => {
        fetchDoctors()
            .then((data) => {
                setDoctors(data);
            })
            .catch((error) => {
                console.error("Error fetching doctors:", error);
                setDoctors([]);
            });
    }, []);

    useEffect(() => {
        setShowAll(clinic?.showAllDoctorsInBot || false);
        setTwoColumns(clinic?.doctorButtonsInTwoColumns !== false);
    }, [clinic?.showAllDoctorsInBot, clinic?.doctorButtonsInTwoColumns]);

    const handleToggle = async (e) => {
        const value = e.target.checked;
        setLoading(true);
        try {
            const { changDoctorsVisibilityInBot } =
                await import("../http/clinicAPI");
            const updatedClinic = await changDoctorsVisibilityInBot(value);
            if (clinic && typeof clinic.setClinicData === "function") {
                clinic.setClinicData(updatedClinic);
            }
            setShowAll(updatedClinic.showAllDoctorsInBot);
        } catch (error) {
            setShowAll(clinic?.showAllDoctorsInBot || false);
        } finally {
            setLoading(false);
        }
    };

    const handleLayoutToggle = async (e) => {
        const value = e.target.checked;
        setLoading(true);
        try {
            const { changeDoctorButtonLayout } =
                await import("../http/clinicAPI");
            const updatedClinic = await changeDoctorButtonLayout(value);
            if (clinic && typeof clinic.setClinicData === "function") {
                clinic.setClinicData(updatedClinic);
            }
            setTwoColumns(updatedClinic.doctorButtonsInTwoColumns !== false);
        } catch (error) {
            setTwoColumns(clinic?.doctorButtonsInTwoColumns !== false);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setRole("");
    };

    const handleSyncDoctors = async () => {
        if (!role.trim()) {
            snackbar.handleChange(true, "Пожалуйста, укажите роль", "warning");
            return;
        }

        setSyncing(true);
        try {
            const result = await syncDoctorsFromMIS(role);
            if (result.success) {
                snackbar.handleChange(
                    true,
                    `Успешно подгружено врачей из МИС: ${result.addedCount}`,
                    "success",
                );
                handleCloseDialog();
                const updatedDoctors = await fetchDoctors();
                setDoctors(updatedDoctors);
            }
        } catch (error) {
            console.error("Error syncing doctors:", error);
            snackbar.handleChange(
                true,
                "Ошибка при подгрузке врачей из МИС",
                "error",
            );
        } finally {
            setSyncing(false);
        }
    };
    const handleChange = (event, newValue) => {
        if (!tabValues.has(newValue)) {
            return;
        }
        setValue(newValue);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set("tab", newValue);
                return next;
            },
            { replace: true },
        );
    };

    const titleByTab = {
        1: "Врачи клиники",
        2: "Категории врачей",
        3: "Специализации и памятки",
    };

    useEffect(() => {
        if (!tabValues.has(tabFromQuery)) {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("tab", "1");
                    return next;
                },
                { replace: true },
            );
        }
    }, [setSearchParams, tabFromQuery, tabValues]);

    useEffect(() => {
        const currentTab = searchParams.get("tab");
        if (tabValues.has(currentTab) && currentTab !== value) {
            setValue(currentTab);
        }
    }, [searchParams, tabValues, value]);

    return (
        <Container>
            <TabContext value={value}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <TabList
                        aria-label="Tab"
                        onChange={handleChange}
                        textColor="secondary"
                        indicatorColor="secondary"
                    >
                        <Tab label="Врачи" value="1" />
                        <Tab label="Категории" value="2" />
                        <Tab label="Специализации" value="3" />
                    </TabList>
                </Box>
                <Typography
                    variant="h6"
                    sx={{ mt: 2, mb: value === "2" ? 1 : 2 }}
                >
                    {titleByTab[value]}
                </Typography>
                {value === "1" && (
                    <PageHeader
                        showDivider={true}
                        cardSx={{ justifyContent: "flex-start" }}
                        left={
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flexWrap="wrap"
                            >
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() =>
                                        navigate(DOCTORS_ROUTE + "/new")
                                    }
                                    sx={{ height: 40, minHeight: 40, px: 2.5 }}
                                >
                                    Добавить врача
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={handleOpenDialog}
                                    sx={{ height: 40, minHeight: 40, px: 2.5 }}
                                >
                                    Подгрузить врачей из МИС
                                </Button>
                            </Stack>
                        }
                        right={
                            <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flexWrap="wrap"
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!showAll}
                                            onChange={handleToggle}
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >{`Отображение всех врачей в боте
независимо от филиала`}</Typography>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!twoColumns}
                                            onChange={handleLayoutToggle}
                                            color="primary"
                                            disabled={loading}
                                        />
                                    }
                                    label={
                                        <Typography
                                            sx={{ whiteSpace: "pre-wrap" }}
                                        >{`Отображение в боте кнопок
в два столбца`}</Typography>
                                    }
                                />
                            </Stack>
                        }
                    />
                )}
                <TabPanel value="1" sx={{ px: 0 }}>
                    <DoctorsList doctors={doctors} />
                </TabPanel>

                <TabPanel value="2" sx={{ px: 0, pt: 0 }}>
                    <DoctorCategories />
                </TabPanel>

                <TabPanel value="3" sx={{ px: 0, pt: 0 }}>
                    <Specializations />
                </TabPanel>
            </TabContext>

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>Подгрузить врачей из МИС</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Роль врачей"
                        fullWidth
                        variant="outlined"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        helperText='Зайти в МИС Renovatio: Верхняя панель «Персонал» → «Должностные роли» → Столбец "Название"'
                        disabled={syncing}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={syncing}>
                        Отмена
                    </Button>
                    <Button
                        onClick={handleSyncDoctors}
                        variant="contained"
                        disabled={syncing}
                    >
                        {syncing ? "Подгрузка..." : "Подгрузить"}
                    </Button>
                </DialogActions>
            </Dialog>

            <MySnackbar
                open={snackbar.open}
                onClose={snackbar.onClose}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Container>
    );
});

export default Doctors;
